import { useEffect, useState } from 'react';

const MOBILE_QUERY = '(max-width: 600px)';

const detectOS = (): string => {
  // Try navigator.userAgentData (Chromium-based browsers only)
  const uaData = (navigator as any).userAgentData;
  if (uaData && uaData.platform) {
    return uaData.platform;
  }
  // Fallback to userAgent string
  const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
  if (/windows phone/i.test(ua)) return 'Windows Phone';
  if (/win/i.test(ua)) return 'Windows';
  if (/android/i.test(ua)) return 'Android';
  if (/iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream) return 'iOS';
  if (/Macintosh|Mac OS X/.test(ua)) return 'macOS';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Unknown';
};

export function useOS(): string {
  const [os, setOS] = useState('Detecting...');

  useEffect(() => {
    setOS(detectOS());
  }, []);

  return os;
}

export function useMacOs(): boolean {
  return useOS().toLowerCase() === 'macos';
}

function detectBrowser(): string {
  const ua = navigator.userAgent;
  // Check for Opera
  if ((window as any).opr || ua.indexOf(' OPR/') >= 0) return 'Opera';
  // Check for Edge
  if (ua.indexOf('Edg/') >= 0) return 'Edge';
  // Check for Chrome (must be after Edge and Opera)
  if (
    (window as any).chrome &&
    ua.indexOf('Chrome') >= 0 &&
    ua.indexOf('Safari') >= 0
  )
    return 'Chrome';
  // Check for Firefox
  if (
    typeof (window as any).InstallTrigger !== 'undefined' ||
    ua.indexOf('Firefox') >= 0
  )
    return 'Firefox';
  // Check for Safari (must be after Chrome and Edge)
  if (/^((?!chrome|android|crios|fxios|edg).)*safari/i.test(ua))
    return 'Safari';
  // Fallback
  return 'Unknown';
}

export function useBrowser(): string {
  const [browser, setBrowser] = useState('Detecting...');

  useEffect(() => {
    setBrowser(detectBrowser());
  }, []);

  return browser;
}

export function useSafari(): boolean {
  return useBrowser().toLowerCase() === 'safari';
}

function detectEngine(): string {
  const ua = navigator.userAgent;
  // Blink (used by Chrome, Edge, Opera, Brave, Vivaldi, DuckDuckGo desktop, etc.)
  if (
    // Chromium-based browsers
    (window as any).chrome !== undefined ||
    ua.includes('Chrome') ||
    ua.includes('Chromium') ||
    ua.includes('Edg/') ||
    ua.includes('OPR/') ||
    ua.includes('Brave')
  ) {
    return 'Blink';
  }
  // WebKit (used by Safari, all iOS browsers, older versions of others)
  if (
    ua.includes('AppleWebKit') &&
    !ua.includes('Chrome') &&
    !ua.includes('Chromium') &&
    !ua.includes('Edg/') &&
    !ua.includes('OPR/')
  ) {
    return 'WebKit';
  }
  // Gecko (used by Firefox)
  if (ua.includes('Gecko/') && ua.includes('Firefox')) {
    return 'Gecko';
  }
  // Trident (used by old Internet Explorer)
  if (ua.includes('Trident/')) {
    return 'Trident';
  }
  // Fallback
  return 'Unknown';
}

export function useEngine(): string {
  const [engine, setEngine] = useState('Detecting...');

  useEffect(() => {
    setEngine(detectEngine());
  }, []);

  return engine;
}

export function useWebKit(): boolean {
  return useEngine().toLowerCase() === 'webkit';
}

export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_QUERY);
    const updateIsMobile = () => {
      setIsMobile(mediaQuery.matches);
    };
    updateIsMobile();
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', updateIsMobile);
      return () => mediaQuery.removeEventListener('change', updateIsMobile);
    }
  }, []);

  return isMobile;
}
